import React from "react";
import pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import { FileDown } from "lucide-react";

// Register fonts - try different approaches
try {
  if (pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) {
    pdfMake.vfs = pdfFonts.pdfMake.vfs;
  } else if (pdfFonts.vfs) {
    pdfMake.vfs = pdfFonts.vfs;
  } else {
    pdfMake.vfs = pdfFonts;
  }
} catch (error) {
  console.error("Error loading PDF fonts:", error);
}

const PDFExport = ({
  movieAnalysis,
  buttonClassName = "text-white text-decoration-none small d-flex align-items-center",
}) => {
  const handleExportPDF = () => {
    try {
      const docDefinition = {
        content: [
          // ==================== PAGE 1 ====================
          // Story Overview + Plot Summary Card (Combined with Movie Title)
          {
            table: {
              widths: ["*"],
              body: [
                [
                  {
                    stack: [
                      {
                        text: movieAnalysis.movieName,
                        bold: true,
                        fontSize: 18,
                        color: "white",
                        margin: [0, 0, 0, 15],
                      },
                      {
                        text: "Story Overview",
                        bold: true,
                        fontSize: 11,
                        color: "white",
                        margin: [0, 0, 0, 5],
                      },
                      {
                        text: movieAnalysis.storyAnalysis?.storyOverview || "",
                        fontSize: 10,
                        color: "white",
                        margin: [0, 0, 0, 12],
                      },
                      {
                        text: "Plot Summary",
                        bold: true,
                        fontSize: 11,
                        color: "white",
                        margin: [0, 0, 0, 5],
                      },
                      {
                        text: movieAnalysis.storyAnalysis?.plotSummary || "",
                        fontSize: 10,
                        color: "white",
                      },
                    ],
                    fillColor: "#0d6efd",
                    margin: [12, 12, 12, 12],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: (i, node) => 0,
              vLineWidth: (i, node) => 0,
              paddingLeft: (i, node) => 0,
              paddingRight: (i, node) => 0,
              paddingTop: (i, node) => 0,
              paddingBottom: (i, node) => 0,
            },
            margin: [0, 0, 0, 15],
          },

          // Overview Card
          {
            table: {
              widths: ["*"],
              body: [
                [
                  {
                    text: "Overview",
                    style: "cardHeader",
                    fillColor: "#e7f1ff",
                    color: "#0d6efd",
                    border: [true, true, true, false],
                  },
                ],
                [
                  {
                    stack: [
                      {
                        text: [
                          { text: "Genre:  ", bold: true },
                          movieAnalysis.overview?.genre || "N/A",
                        ],
                        fontSize: 10,
                        margin: [0, 0, 0, 4],
                      },
                      {
                        text: [
                          { text: "Duration:  ", bold: true },
                          movieAnalysis.overview?.estimatedDuration || "N/A",
                        ],
                        fontSize: 10,
                        margin: [0, 0, 0, 4],
                      },
                      {
                        text: [
                          { text: "Setting:  ", bold: true },
                          movieAnalysis.overview?.setting || "N/A",
                        ],
                        fontSize: 10,
                        margin: [0, 0, 0, 4],
                      },
                      ...(movieAnalysis.overview?.subGenres &&
                      movieAnalysis.overview.subGenres.length > 0
                        ? [
                            {
                              text: "Subgenres:",
                              bold: true,
                              fontSize: 10,
                              margin: [0, 8, 0, 5],
                            },
                            {
                              text: movieAnalysis.overview.subGenres
                                .map((sg, idx) => {
                                  return {
                                    text:
                                      sg +
                                      (idx <
                                      movieAnalysis.overview.subGenres.length -
                                        1
                                        ? "  •  "
                                        : ""),
                                    background: "#0d6efd",
                                    color: "white",
                                    fontSize: 9,
                                  };
                                })
                                .reduce((acc, curr) => acc.concat(curr), []),
                              fontSize: 9,
                            },
                          ]
                        : []),
                    ],
                    margin: [12, 10, 12, 12],
                    border: [true, false, true, true],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: (i, node) => 1,
              vLineWidth: (i, node) => 1,
              hLineColor: (i, node) => "#0d6efd",
              vLineColor: (i, node) => "#0d6efd",
              paddingLeft: (i, node) => 0,
              paddingRight: (i, node) => 0,
              paddingTop: (i, node) => 0,
              paddingBottom: (i, node) => 0,
            },
            margin: [0, 0, 0, 15],
          },

          // Languages Card
          {
            table: {
              widths: ["*"],
              body: [
                [
                  {
                    text: "Languages",
                    style: "cardHeader",
                    fillColor: "#cff4fc",
                    color: "#0dcaf0",
                    border: [true, true, true, false],
                  },
                ],
                [
                  {
                    stack: [
                      {
                        text: [
                          { text: "Primary:  ", bold: true },
                          movieAnalysis.languageAnalysis?.primaryLanguage ||
                            "N/A",
                        ],
                        fontSize: 10,
                        margin: [0, 0, 0, 8],
                      },
                      ...(movieAnalysis.languageAnalysis?.languages &&
                      movieAnalysis.languageAnalysis.languages.length > 0
                        ? movieAnalysis.languageAnalysis.languages.map(
                            (lang) => ({
                              text: [
                                { text: `${lang.language}:  `, bold: true },
                                `${lang.percentage}%`,
                              ],
                              fontSize: 10,
                              margin: [0, 0, 0, 4],
                            })
                          )
                        : []),
                    ],
                    margin: [12, 10, 12, 12],
                    border: [true, false, true, true],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: (i, node) => 1,
              vLineWidth: (i, node) => 1,
              hLineColor: (i, node) => "#0dcaf0",
              vLineColor: (i, node) => "#0dcaf0",
              paddingLeft: (i, node) => 0,
              paddingRight: (i, node) => 0,
              paddingTop: (i, node) => 0,
              paddingBottom: (i, node) => 0,
            },
            margin: [0, 0, 0, 15],
          },

          // Marketing Card
          {
            table: {
              widths: ["*"],
              body: [
                [
                  {
                    text: "Marketing",
                    style: "cardHeader",
                    fillColor: "#d1e7dd",
                    color: "#198754",
                    border: [true, true, true, false],
                  },
                ],
                [
                  {
                    stack: [
                      {
                        text: [
                          { text: "Type:  ", bold: true },
                          movieAnalysis.marketingInsights?.suggestedPromoType
                            ? movieAnalysis.marketingInsights.suggestedPromoType
                                .charAt(0)
                                .toUpperCase() +
                              movieAnalysis.marketingInsights.suggestedPromoType.slice(
                                1
                              )
                            : "N/A",
                        ],
                        fontSize: 10,
                        margin: [0, 0, 0, 4],
                      },
                      {
                        text: [
                          { text: "Audience:  ", bold: true },
                          movieAnalysis.marketingInsights?.targetAudience ||
                            "N/A",
                        ],
                        fontSize: 10,
                        margin: [0, 0, 0, 4],
                      },
                      {
                        text: [
                          { text: "Trailer:  ", bold: true },
                          movieAnalysis.marketingInsights?.trailerLength
                            ? movieAnalysis.marketingInsights.trailerLength
                                .charAt(0)
                                .toUpperCase() +
                              movieAnalysis.marketingInsights.trailerLength.slice(
                                1
                              )
                            : "N/A",
                        ],
                        fontSize: 10,
                      },
                    ],
                    margin: [12, 10, 12, 12],
                    border: [true, false, true, true],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: (i, node) => 1,
              vLineWidth: (i, node) => 1,
              hLineColor: (i, node) => "#198754",
              vLineColor: (i, node) => "#198754",
              paddingLeft: (i, node) => 0,
              paddingRight: (i, node) => 0,
              paddingTop: (i, node) => 0,
              paddingBottom: (i, node) => 0,
            },
          },

          // ==================== PAGE 2 ====================
          { text: "", pageBreak: "before" },

          // Key Characters Card
          {
            table: {
              widths: ["*"],
              body: [
                [
                  {
                    text: "Key Characters",
                    style: "cardHeader",
                    fillColor: "#fff3cd",
                    color: "#856404",
                    border: [true, true, true, false],
                  },
                ],
                [
                  {
                    stack:
                      movieAnalysis.keyCharacters &&
                      movieAnalysis.keyCharacters.length > 0
                        ? movieAnalysis.keyCharacters.map((char, idx) => ({
                            stack: [
                              {
                                columns: [
                                  {
                                    stack: [
                                      {
                                        text: char.name,
                                        bold: true,
                                        fontSize: 11,
                                        color: "#212529",
                                        margin: [0, 0, 0, 3],
                                      },
                                      {
                                        text: [
                                          {
                                            text: "Role:  ",
                                            bold: true,
                                            fontSize: 9,
                                            color: "#6c757d",
                                          },
                                          {
                                            text:
                                              char.role
                                                .charAt(0)
                                                .toUpperCase() +
                                              char.role.slice(1),
                                            fontSize: 9,
                                            color: "#6c757d",
                                          },
                                        ],
                                      },
                                    ],
                                    width: "*",
                                  },
                                  {
                                    table: {
                                      widths: ["*"],
                                      body: [
                                        [
                                          {
                                            text: `${char.importance}/10`,
                                            fontSize: 9,
                                            color: "black",
                                            bold: true,
                                            fillColor: "#ffc107",
                                            alignment: "center",
                                            border: [
                                              false,
                                              false,
                                              false,
                                              false,
                                            ],
                                            margin: [4, 4, 4, 4],
                                          },
                                        ],
                                        [
                                          {
                                            text: "importance",
                                            fontSize: 7,
                                            color: "#6c757d",
                                            alignment: "center",
                                            border: [
                                              false,
                                              false,
                                              false,
                                              false,
                                            ],
                                            margin: [2, 0, 2, 2],
                                          },
                                        ],
                                      ],
                                    },
                                    layout: "noBorders",
                                    width: 60,
                                  },
                                ],
                                margin: [0, 0, 0, 5],
                              },
                              {
                                text: char.description,
                                fontSize: 9,
                                color: "#212529",
                                margin: [
                                  0,
                                  0,
                                  0,
                                  idx < movieAnalysis.keyCharacters.length - 1
                                    ? 12
                                    : 0,
                                ],
                              },
                              ...(idx < movieAnalysis.keyCharacters.length - 1
                                ? [
                                    {
                                      canvas: [
                                        {
                                          type: "line",
                                          x1: 0,
                                          y1: 0,
                                          x2: 515,
                                          y2: 0,
                                          lineWidth: 0.5,
                                          lineColor: "#dee2e6",
                                        },
                                      ],
                                      margin: [0, 0, 0, 12],
                                    },
                                  ]
                                : []),
                            ],
                          }))
                        : [
                            {
                              text: "No character data available",
                              fontSize: 10,
                              color: "#6c757d",
                            },
                          ],
                    margin: [12, 10, 12, 12],
                    border: [true, false, true, true],
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: (i, node) => 1,
              vLineWidth: (i, node) => 1,
              hLineColor: (i, node) => "#ffc107",
              vLineColor: (i, node) => "#ffc107",
              paddingLeft: (i, node) => 0,
              paddingRight: (i, node) => 0,
              paddingTop: (i, node) => 0,
              paddingBottom: (i, node) => 0,
            },
            margin: [0, 0, 0, 15],
          },

          // ==================== PAGE 3 ====================
          ...(movieAnalysis.toneAndMood || movieAnalysis.storyAnalysis
            ? [
                { text: "", pageBreak: "before" },

                // Tone & Mood Card
                ...(movieAnalysis.toneAndMood
                  ? [
                      {
                        table: {
                          widths: ["*"],
                          body: [
                            [
                              {
                                text: "Tone & Mood",
                                style: "cardHeader",
                                fillColor: "#e2e3e5",
                                color: "#6c757d",
                                border: [true, true, true, false],
                              },
                            ],
                            [
                              {
                                columns: [
                                  {
                                    width: "33.33%",
                                    stack: [
                                      {
                                        text: "Primary Tone",
                                        bold: true,
                                        fontSize: 10,
                                        color: "#6c757d",
                                        alignment: "center",
                                        margin: [0, 0, 0, 8],
                                      },
                                      {
                                        table: {
                                          widths: ["*"],
                                          body: [
                                            [
                                              {
                                                text: movieAnalysis.toneAndMood
                                                  .primaryTone,
                                                fontSize: 9,
                                                bold: true,
                                                color: "white",
                                                fillColor: "#6c757d",
                                                alignment: "center",
                                                border: [
                                                  false,
                                                  false,
                                                  false,
                                                  false,
                                                ],
                                                margin: [6, 5, 6, 5],
                                              },
                                            ],
                                          ],
                                        },
                                        layout: "noBorders",
                                      },
                                    ],
                                    margin: [5, 5, 5, 5],
                                  },
                                  {
                                    width: "33.33%",
                                    stack: [
                                      {
                                        text: "Intensity Level",
                                        bold: true,
                                        fontSize: 10,
                                        color: "#6c757d",
                                        alignment: "center",
                                        margin: [0, 0, 0, 8],
                                      },
                                      {
                                        table: {
                                          widths: ["*"],
                                          body: [
                                            [
                                              {
                                                text: movieAnalysis.toneAndMood
                                                  .intensityLevel,
                                                fontSize: 9,
                                                bold: true,
                                                color: "white",
                                                fillColor: "#6c757d",
                                                alignment: "center",
                                                border: [
                                                  false,
                                                  false,
                                                  false,
                                                  false,
                                                ],
                                                margin: [6, 5, 6, 5],
                                              },
                                            ],
                                          ],
                                        },
                                        layout: "noBorders",
                                      },
                                    ],
                                    margin: [5, 5, 5, 5],
                                  },
                                  {
                                    width: "33.34%",
                                    stack: [
                                      {
                                        text: "Mood Progression",
                                        bold: true,
                                        fontSize: 10,
                                        color: "#6c757d",
                                        alignment: "center",
                                        margin: [0, 0, 0, 8],
                                      },
                                      {
                                        text: movieAnalysis.toneAndMood
                                          .moodProgression,
                                        fontSize: 9,
                                        alignment: "center",
                                        margin: [5, 4, 5, 4],
                                      },
                                    ],
                                    margin: [5, 5, 5, 5],
                                  },
                                ],
                                margin: [7, 10, 7, 10],
                                border: [true, false, true, true],
                              },
                            ],
                          ],
                        },
                        layout: {
                          hLineWidth: (i, node) => 1,
                          vLineWidth: (i, node) => 1,
                          hLineColor: (i, node) => "#6c757d",
                          vLineColor: (i, node) => "#6c757d",
                          paddingLeft: (i, node) => 0,
                          paddingRight: (i, node) => 0,
                          paddingTop: (i, node) => 0,
                          paddingBottom: (i, node) => 0,
                        },
                        margin: [0, 0, 0, 15],
                      },
                    ]
                  : []),

                // Story Analysis Card
                ...(movieAnalysis.storyAnalysis
                  ? [
                      {
                        table: {
                          widths: ["*"],
                          body: [
                            [
                              {
                                text: "Story Analysis",
                                style: "cardHeader",
                                fillColor: "#d3d3d4",
                                color: "#212529",
                                border: [true, true, true, false],
                              },
                            ],
                            [
                              {
                                stack: [
                                  // Plot Summary
                                  {
                                    text: "Plot Summary",
                                    bold: true,
                                    fontSize: 11,
                                    color: "#212529",
                                    margin: [0, 0, 0, 5],
                                  },
                                  {
                                    text:
                                      movieAnalysis.storyAnalysis.plotSummary ||
                                      "",
                                    fontSize: 9,
                                    margin: [0, 0, 0, 15],
                                  },

                                  // Separator line
                                  {
                                    canvas: [
                                      {
                                        type: "line",
                                        x1: 0,
                                        y1: 0,
                                        x2: 515,
                                        y2: 0,
                                        lineWidth: 1,
                                        lineColor: "#dee2e6",
                                      },
                                    ],
                                    margin: [0, 0, 0, 15],
                                  },

                                  // Main Conflict
                                  {
                                    text: "Main Conflict",
                                    bold: true,
                                    fontSize: 11,
                                    color: "#212529",
                                    margin: [0, 0, 0, 5],
                                  },
                                  {
                                    text:
                                      movieAnalysis.storyAnalysis
                                        .mainConflict || "",
                                    fontSize: 9,
                                    margin: [0, 0, 0, 15],
                                  },

                                  // Separator line
                                  {
                                    canvas: [
                                      {
                                        type: "line",
                                        x1: 0,
                                        y1: 0,
                                        x2: 515,
                                        y2: 0,
                                        lineWidth: 1,
                                        lineColor: "#dee2e6",
                                      },
                                    ],
                                    margin: [0, 0, 0, 15],
                                  },

                                  // Themes
                                  ...(movieAnalysis.storyAnalysis.themes &&
                                  movieAnalysis.storyAnalysis.themes.length > 0
                                    ? [
                                        {
                                          text: "Themes",
                                          bold: true,
                                          fontSize: 11,
                                          color: "#212529",
                                          margin: [0, 0, 0, 5],
                                        },
                                        {
                                          text: movieAnalysis.storyAnalysis.themes
                                            .map((theme, idx) => {
                                              return {
                                                text:
                                                  theme +
                                                  (idx <
                                                  movieAnalysis.storyAnalysis
                                                    .themes.length -
                                                    1
                                                    ? "  •  "
                                                    : ""),
                                                color: "black",
                                                fontSize: 9,
                                              };
                                            })
                                            .reduce(
                                              (acc, curr) => acc.concat(curr),
                                              []
                                            ),
                                          fontSize: 9,
                                        },
                                        { text: "", margin: [0, 0, 0, 15] },

                                        // Separator line
                                        {
                                          canvas: [
                                            {
                                              type: "line",
                                              x1: 0,
                                              y1: 0,
                                              x2: 515,
                                              y2: 0,
                                              lineWidth: 1,
                                              lineColor: "#dee2e6",
                                            },
                                          ],
                                          margin: [0, 0, 0, 15],
                                        },
                                      ]
                                    : []),

                                  // Act Structure
                                  ...(movieAnalysis.storyAnalysis.actStructure
                                    ? [
                                        {
                                          text: "Act Structure",
                                          bold: true,
                                          fontSize: 11,
                                          color: "#212529",
                                          margin: [0, 0, 0, 5],
                                        },
                                        ...Object.entries(
                                          movieAnalysis.storyAnalysis
                                            .actStructure
                                        ).map(([act, desc]) => ({
                                          text: [
                                            {
                                              text: `${
                                                act.charAt(0).toUpperCase() +
                                                act.slice(1)
                                              }:  `,
                                              bold: true,
                                              fontSize: 9,
                                            },
                                            { text: desc, fontSize: 9 },
                                          ],
                                          margin: [0, 0, 0, 4],
                                        })),
                                        { text: "", margin: [0, 0, 0, 15] },
                                        // Separator line
                                        {
                                          canvas: [
                                            {
                                              type: "line",
                                              x1: 0,
                                              y1: 0,
                                              x2: 515,
                                              y2: 0,
                                              lineWidth: 1,
                                              lineColor: "#dee2e6",
                                            },
                                          ],
                                          margin: [0, 0, 0, 15],
                                        },
                                      ]
                                    : []),

                                  // Key Story Beats
                                  ...(movieAnalysis.storyAnalysis
                                    .keyStoryBeats &&
                                  movieAnalysis.storyAnalysis.keyStoryBeats
                                    .length > 0
                                    ? [
                                        {
                                          text: "Key Story Beats",
                                          bold: true,
                                          fontSize: 11,
                                          color: "#212529",
                                          margin: [0, 0, 0, 5],
                                        },
                                        ...movieAnalysis.storyAnalysis.keyStoryBeats.map(
                                          (beat) => ({
                                            text: [
                                              {
                                                text: `${beat.beat}:  `,
                                                bold: true,
                                                fontSize: 9,
                                              },
                                              {
                                                text: beat.significance,
                                                fontSize: 9,
                                              },
                                            ],
                                            margin: [0, 0, 0, 6],
                                          })
                                        ),
                                      ]
                                    : []),
                                ],
                                margin: [12, 10, 12, 12],
                                border: [true, false, true, true],
                              },
                            ],
                          ],
                        },
                        layout: {
                          hLineWidth: (i, node) => 1,
                          vLineWidth: (i, node) => 1,
                          hLineColor: (i, node) => "#212529",
                          vLineColor: (i, node) => "#212529",
                          paddingLeft: (i, node) => 0,
                          paddingRight: (i, node) => 0,
                          paddingTop: (i, node) => 0,
                          paddingBottom: (i, node) => 0,
                        },
                      },
                    ]
                  : []),
              ]
            : []),

          // ==================== PAGE 4 ====================
          ...(movieAnalysis.storyAnalysis &&
          (movieAnalysis.storyAnalysis.emotionalArcs ||
            movieAnalysis.storyAnalysis.plotTwists ||
            movieAnalysis.storyAnalysis.climax ||
            movieAnalysis.storyAnalysis.resolution)
            ? [
                { text: "", pageBreak: "before" },

                // Emotional Arcs & Plot Twists Card
                {
                  table: {
                    widths: ["*"],
                    body: [
                      [
                        {
                          text: "Emotional Arcs & Plot Twists",
                          style: "cardHeader",
                          fillColor: "#d3d3d4",
                          color: "#212529",
                          border: [true, true, true, false],
                        },
                      ],
                      [
                        {
                          stack: [
                            // Emotional Arcs
                            ...(movieAnalysis.storyAnalysis.emotionalArcs &&
                            movieAnalysis.storyAnalysis.emotionalArcs.length > 0
                              ? [
                                  {
                                    text: "Emotional Arcs",
                                    bold: true,
                                    fontSize: 11,
                                    color: "#212529",
                                    margin: [0, 0, 0, 5],
                                  },
                                  ...movieAnalysis.storyAnalysis.emotionalArcs.map(
                                    (arc) => ({
                                      text: [
                                        {
                                          text: `${arc.character}:  `,
                                          bold: true,
                                          fontSize: 9,
                                        },
                                        { text: arc.arc, fontSize: 9 },
                                      ],
                                      margin: [0, 0, 0, 4],
                                    })
                                  ),
                                  { text: "", margin: [0, 0, 0, 8] },
                                ]
                              : []),

                            // Plot Twists
                            ...(movieAnalysis.storyAnalysis.plotTwists &&
                            movieAnalysis.storyAnalysis.plotTwists.length > 0
                              ? [
                                  {
                                    text: "Plot Twists",
                                    bold: true,
                                    fontSize: 11,
                                    color: "#212529",
                                    margin: [0, 0, 0, 5],
                                  },
                                  ...movieAnalysis.storyAnalysis.plotTwists.map(
                                    (twist, idx) => ({
                                      text: `${idx + 1}. ${twist}`,
                                      fontSize: 9,
                                      margin: [0, 0, 0, 4],
                                    })
                                  ),
                                ]
                              : []),
                          ],
                          margin: [12, 10, 12, 12],
                          border: [true, false, true, true],
                        },
                      ],
                    ],
                  },
                  layout: {
                    hLineWidth: (i, node) => 1,
                    vLineWidth: (i, node) => 1,
                    hLineColor: (i, node) => "#212529",
                    vLineColor: (i, node) => "#212529",
                    paddingLeft: (i, node) => 0,
                    paddingRight: (i, node) => 0,
                    paddingTop: (i, node) => 0,
                    paddingBottom: (i, node) => 0,
                  },
                  margin: [0, 0, 0, 15],
                },

                // Climax & Resolution Card
                {
                  table: {
                    widths: ["*"],
                    body: [
                      [
                        {
                          text: "Climax & Resolution",
                          style: "cardHeader",
                          fillColor: "#d3d3d4",
                          color: "#212529",
                          border: [true, true, true, false],
                        },
                      ],
                      [
                        {
                          stack: [
                            {
                              text: [
                                { text: "Climax:  ", bold: true, fontSize: 10 },
                                {
                                  text:
                                    movieAnalysis.storyAnalysis.climax || "",
                                  fontSize: 9,
                                },
                              ],
                              margin: [0, 0, 0, 10],
                            },
                            {
                              text: [
                                {
                                  text: "Resolution:  ",
                                  bold: true,
                                  fontSize: 10,
                                },
                                {
                                  text:
                                    movieAnalysis.storyAnalysis.resolution ||
                                    "",
                                  fontSize: 9,
                                },
                              ],
                            },
                          ],
                          margin: [12, 10, 12, 12],
                          border: [true, false, true, true],
                        },
                      ],
                    ],
                  },
                  layout: {
                    hLineWidth: (i, node) => 1,
                    vLineWidth: (i, node) => 1,
                    hLineColor: (i, node) => "#212529",
                    vLineColor: (i, node) => "#212529",
                    paddingLeft: (i, node) => 0,
                    paddingRight: (i, node) => 0,
                    paddingTop: (i, node) => 0,
                    paddingBottom: (i, node) => 0,
                  },
                },
              ]
            : []),
        ],

        styles: {
          mainTitle: {
            fontSize: 24,
            bold: true,
          },
          cardHeader: {
            fontSize: 13,
            bold: true,
            margin: [12, 10, 12, 10],
          },
        },

        defaultStyle: {
          font: "Roboto",
        },

        pageMargins: [20, 20, 20, 20],
      };

      const fileName = `${movieAnalysis.movieName.replace(
        /[^a-z0-9]/gi,
        "_"
      )}_Analysis.pdf`;
      pdfMake.createPdf(docDefinition).download(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <button
      onClick={handleExportPDF}
      className={buttonClassName}
      style={{
        fontSize: "0.8rem",
        cursor: "pointer",
        background: "none",
        border: "none",
      }}
      title="Export Analysis as PDF"
    >
      <FileDown size={12} className="me-1" />
      Export PDF
    </button>
  );
};

export default PDFExport;
